import { EventClient } from "@tanstack/devtools-event-client";
import {
	Check,
	ChevronDown,
	ChevronRight,
	Copy,
	Download,
	Edit2,
	RefreshCw,
	Upload,
	X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ServiceOptionCode } from "@/api/types/moneygram";
import type { RampsState } from "../stores/ramps-store";
import { rampsActions, rampsStore } from "../stores/ramps-store";

type EventMap = {
	"ramps-store-devtools:state": Partial<RampsState>;
};

class RampsStoreDevtoolsEventClient extends EventClient<EventMap> {
	constructor() {
		super({
			pluginId: "ramps-store-devtools",
		});
	}
}

const rdec = new RampsStoreDevtoolsEventClient();

// Subscribe to ramps store changes
rampsStore.subscribe(() => {
	rdec.emit("state", rampsStore.state);
});

// Default collapsed state for sections (true = collapsed)
const DEFAULT_COLLAPSED_STATE: Record<string, boolean> = {
	"Flow Control": false,
	Calculator: false,
	"Selected Bank Choice": true,
	Phone: true,
	"Personal Information": true,
	Address: true,
	Identification: true,
	"Bank Fields (Digital Offramp)": true,
	"Returning User (Legacy Profile)": true,
	Transaction: false,
	"Quote & Validation": true,
	"Selected Location": true,
	"Cash-In Location": false,
	"Profile Tracking": false,
	"KYC Verification": false,
	"Locked Fields": true,
};

function DevtoolPanel() {
	const [state, setState] = useState<Partial<RampsState>>(
		() => rampsStore.state,
	);

	// Load collapsed state from localStorage
	const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
		try {
			const saved = localStorage.getItem("ramps-devtools-collapsed");
			return saved ? JSON.parse(saved) : DEFAULT_COLLAPSED_STATE;
		} catch {
			return DEFAULT_COLLAPSED_STATE;
		}
	});

	// Save collapsed state to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem("ramps-devtools-collapsed", JSON.stringify(collapsed));
	}, [collapsed]);

	const toggleSection = (title: string) => {
		setCollapsed((prev) => ({
			...prev,
			[title]: !prev[title],
		}));
	};

	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleReset = () => {
		if (
			confirm(
				"Are you sure you want to reset the entire store? This cannot be undone.",
			)
		) {
			rampsActions.resetForm();
		}
	};

	const handleExport = () => {
		const dataStr = JSON.stringify(state, null, 2);
		const dataBlob = new Blob([dataStr], { type: "application/json" });
		const url = URL.createObjectURL(dataBlob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `ramps-store-${new Date().toISOString().slice(0, 10)}.json`;
		link.click();
		URL.revokeObjectURL(url);
	};

	const handleImport = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (event) => {
			try {
				const importedState = JSON.parse(event.target?.result as string);
				// Merge imported state with current state
				rampsStore.setState((prev) => ({
					...prev,
					...importedState,
				}));
				alert("State imported successfully!");
			} catch (_error) {
				alert("Failed to import state. Please check the file format.");
			}
		};
		reader.readAsText(file);
		// Reset input value to allow importing the same file again
		e.target.value = "";
	};

	const handleCopyAll = () => {
		const dataStr = JSON.stringify(state, null, 2);
		navigator.clipboard.writeText(dataStr).then(
			() => {
				alert("State copied to clipboard!");
			},
			() => {
				alert("Failed to copy to clipboard");
			},
		);
	};

	useEffect(() => {
		return rdec.on("state", (e) => setState(e.payload));
	}, []);

	return (
		<div className="flex flex-col h-full">
			{/* Action Toolbar */}
			<div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-3 z-10">
				<div className="flex items-center justify-between mb-2">
					<h2 className="text-sm font-bold text-blue-400">
						Ramps Store DevTools
					</h2>
				</div>
				<div className="flex gap-2 flex-wrap">
					<button
						type="button"
						onClick={handleReset}
						className="flex items-center gap-1 px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
						title="Reset Store"
					>
						<RefreshCw className="w-3 h-3" />
						Reset
					</button>
					<button
						type="button"
						onClick={handleExport}
						className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
						title="Export State"
					>
						<Download className="w-3 h-3" />
						Export
					</button>
					<button
						type="button"
						onClick={handleImport}
						className="flex items-center gap-1 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
						title="Import State"
					>
						<Upload className="w-3 h-3" />
						Import
					</button>
					<button
						type="button"
						onClick={handleCopyAll}
						className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
						title="Copy All"
					>
						<Copy className="w-3 h-3" />
						Copy All
					</button>
				</div>
				<input
					ref={fileInputRef}
					type="file"
					accept=".json"
					onChange={handleFileChange}
					className="hidden"
				/>
			</div>

			{/* Scrollable Content */}
			<div className="flex-1 p-4 space-y-6 overflow-y-auto">
				{/* Flow Control */}
				<Section
					title="Flow Control"
					isCollapsed={collapsed["Flow Control"]}
					onToggle={() => toggleSection("Flow Control")}
				>
					<EditableDropdown
						label="Current Step"
						value={state?.currentStep || "calculator"}
						options={[
							"calculator",
							"phone",
							"name",
							"address",
							"date-of-birth",
							"identification",
							"kyc-verification",
							"digital-offramp-info",
							"additional-info",
							"review",
							"status",
						]}
						onChange={(value) =>
							rampsActions.setCurrentStep(value as RampsState["currentStep"])
						}
					/>
				</Section>

				{/* Calculator Data */}
				<Section
					title="Calculator"
					isCollapsed={collapsed.Calculator}
					onToggle={() => toggleSection("Calculator")}
				>
					<EditableTextField
						label="Residency Country"
						value={state?.residencyCountry || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								residencyCountry: value,
							}))
						}
					/>
					<EditableTextField
						label="Payout Country"
						value={state?.payoutCountry || ""}
						onChange={(value) => rampsActions.setPayoutCountry(value)}
					/>
					<EditableTextField
						label="State"
						value={state?.state || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({ ...prev, state: value }))
						}
					/>
					<EditableTextField
						label="Amount"
						value={state?.amount || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({ ...prev, amount: value }))
						}
					/>
					<EditableTextField
						label="Currency"
						value={state?.currency || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({ ...prev, currency: value }))
						}
					/>
					<EditableNumberField
						label="Min Amount"
						value={state?.minAmount}
						onChange={(value) => rampsActions.setMinAmount(value)}
					/>
					<EditableNumberField
						label="Max Amount"
						value={state?.maxAmount}
						onChange={(value) => rampsActions.setMaxAmount(value)}
					/>
					<Field label="Payout Currency" value={state?.payoutCurrency} />
					<EditableTextField
						label="Exchange Rate"
						value={state?.exchangeRate || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({ ...prev, exchangeRate: value }))
						}
					/>
					<EditableTextField
						label="Fees"
						value={state?.fees || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({ ...prev, fees: value }))
						}
					/>
					<EditableTextField
						label="Total"
						value={state?.total || ""}
						onChange={(value) =>
							rampsStore.setState((prev) => ({ ...prev, total: value }))
						}
					/>
					<EditableDropdown
						label="Service Option"
						value={state?.serviceOption || "WILL_CALL"}
						options={[
							"WILL_CALL",
							"BANK_DEPOSIT",
							"DIRECT_TO_ACCT",
							"CASH_PICKUP",
						]}
						onChange={(value) =>
							rampsActions.setServiceOption(value as ServiceOptionCode)
						}
					/>
					<Field
						label="Service Option Routing Code"
						value={state?.serviceOptionRoutingCode}
					/>
					<BooleanToggle
						label="Valid Bank Account"
						value={state?.isValidBankAccount ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								isValidBankAccount: value,
							}))
						}
					/>
				</Section>

				{/* Selected Bank Choice */}
				{state?.selectedBankChoice && (
					<Section
						title="Selected Bank Choice"
						isCollapsed={collapsed["Selected Bank Choice"]}
						onToggle={() => toggleSection("Selected Bank Choice")}
					>
						<Field
							label="Choice Name"
							value={state.selectedBankChoice.choiceName}
						/>
						<Field
							label="Receive Agent ID"
							value={state.selectedBankChoice.receiveAgentId}
						/>
						<Field
							label="Attr Name"
							value={state.selectedBankChoice.attrName}
						/>
						<Field
							label="Attr Value"
							value={state.selectedBankChoice.attrValue}
						/>
						<Field
							label="Display Order"
							value={state.selectedBankChoice.displayOrder}
						/>
					</Section>
				)}

				{/* Phone Verification */}
				<Section
					title="Phone"
					isCollapsed={collapsed.Phone}
					onToggle={() => toggleSection("Phone")}
				>
					<Field label="Country Code" value={state?.phoneCountryCode} />
					<Field label="Phone Number" value={state?.phone} />
				</Section>

				{/* Personal Information */}
				<Section
					title="Personal Information"
					isCollapsed={collapsed["Personal Information"]}
					onToggle={() => toggleSection("Personal Information")}
				>
					<Field label="First Name" value={state?.firstName} />
					<Field label="Middle Name" value={state?.middleName} />
					<Field label="Last Name" value={state?.lastName} />
					<Field label="Second Last Name" value={state?.secondLastName} />
					<BooleanToggle
						label="Legal Name Confirmed"
						value={state?.isLegalNameConfirmed ?? false}
						onChange={(value) => rampsActions.confirmLegalName(value)}
					/>
					<Field label="Date of Birth" value={state?.dateOfBirth} />
					<Field label="Birth Country" value={state?.birthCountryCode} />
					<Field label="Birth City" value={state?.birthCity} />
					<Field label="Email" value={state?.email} />
					<Field label="Gender Code" value={state?.genderCode} />
					<Field
						label="Citizenship Country"
						value={state?.citizenshipCountryCode}
					/>
					<Field
						label="Notification Language"
						value={state?.notificationLanguagePreference}
					/>
					<BooleanToggle
						label="PEP"
						value={state?.politicalExposedPerson ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								politicalExposedPerson: value,
							}))
						}
					/>
				</Section>

				{/* Address Information */}
				<Section
					title="Address"
					isCollapsed={collapsed.Address}
					onToggle={() => toggleSection("Address")}
				>
					<Field label="Address Line 1" value={state?.address} />
					<Field label="Address Line 2" value={state?.address2} />
					<Field label="Address Line 3" value={state?.address3} />
					<Field label="City" value={state?.city} />
					<Field label="Zip Code" value={state?.zipCode} />
					<Field label="Country" value={state?.addressCountry} />
					<Field label="State" value={state?.addressState} />
					<BooleanToggle
						label="Manual Address"
						value={state?.isManualAddress ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								isManualAddress: value,
							}))
						}
					/>
				</Section>

				{/* Identification */}
				<Section
					title="Identification"
					isCollapsed={collapsed.Identification}
					onToggle={() => toggleSection("Identification")}
				>
					<Field label="ID Type" value={state?.idType} />
					<MaskedField label="ID Number" value={state?.idNumber || ""} />
					<Field label="Issue Country" value={state?.idIssueCountry} />
					<Field label="Issue State" value={state?.idIssueState} />
				</Section>

				{/* Bank Fields (GFFP) */}
				{state?.bankFieldsGFFP && (
					<Section
						title="Bank Fields (Digital Offramp)"
						isCollapsed={collapsed["Bank Fields (Digital Offramp)"]}
						onToggle={() => toggleSection("Bank Fields (Digital Offramp)")}
					>
						<div className="col-span-2 space-y-3">
							<div>
								<div className="text-xs font-semibold text-gray-400 mb-2">
									Schema Fields:
								</div>
								<div className="text-xs text-gray-200 space-y-1">
									{state.bankFieldsGFFP.schema.map((field) => (
										<div key={field.field} className="flex gap-2">
											<span className="text-blue-400">{field.field}</span>
											<span className="text-gray-500">({field.dataType})</span>
											{field.required && (
												<span className="text-red-400">*</span>
											)}
										</div>
									))}
								</div>
							</div>
							<div>
								<div className="text-xs font-semibold text-gray-400 mb-2">
									Values:
								</div>
								<div className="text-xs text-gray-200">
									<pre className="bg-gray-800 p-2 rounded overflow-x-auto">
										{JSON.stringify(state.bankFieldsGFFP.values, null, 2)}
									</pre>
								</div>
							</div>
						</div>
					</Section>
				)}

				{/* Returning User */}
				<Section
					title="Returning User (Legacy Profile)"
					isCollapsed={collapsed["Returning User (Legacy Profile)"]}
					onToggle={() => toggleSection("Returning User (Legacy Profile)")}
				>
					<Field
						label="Has Digital Profile"
						value={
							state?.digitalProfileId
								? `✅ ${state.digitalProfileId.slice(0, 12)}...`
								: "No"
						}
					/>
					<Field
						label="Digital Profile ID"
						value={state?.digitalProfileId || "—"}
						copyable={true}
					/>
					<Field
						label="Enrol in Rewards"
						value={state?.enrolInRewards || "—"}
					/>
					<Field
						label="Notification Preferences"
						value={state?.notificationPreferences || "—"}
					/>
					{state?.selectedBankAccount && (
						<>
							<Field
								label="Account Profile ID"
								value={state.selectedBankAccount.targetAccountProfileId}
							/>
							<Field
								label="Account Number (Masked)"
								value={state.selectedBankAccount.accountNumberMasked}
							/>
						</>
					)}
					{!state?.selectedBankAccount && state?.digitalProfileId && (
						<div className="col-span-2 text-xs text-yellow-400">
							⚠️ Has profile but no bank account selected
						</div>
					)}
				</Section>

				{/* Transaction Data */}
				<Section
					title="Transaction"
					isCollapsed={collapsed.Transaction}
					onToggle={() => toggleSection("Transaction")}
				>
					<EditableTextField
						label="Transaction ID"
						value={state?.transactionId || ""}
						onChange={(value) => rampsActions.setTransactionId(value)}
					/>
					<EditableTextField
						label="Wallet Address"
						value={state?.walletAddress || ""}
						onChange={(value) => rampsActions.setWalletAddress(value)}
					/>
					<EditableDropdown
						label="API Version"
						value={state?.apiVersion || "v3"}
						options={["v2", "v3", "v5"]}
						onChange={(value) =>
							rampsActions.setApiVersion(value as "v2" | "v3" | "v5")
						}
					/>
					<MaskedField label="Token" value={state?.token || ""} />
					<Field
						label="Reference Number"
						value={state?.commitResponse?.referenceNumber}
						copyable={true}
					/>
				</Section>

				{/* Quote & Validation */}
				<Section
					title="Quote & Validation"
					isCollapsed={collapsed["Quote & Validation"]}
					onToggle={() => toggleSection("Quote & Validation")}
				>
					<Field
						label="Existing Profile"
						value={state?.quoteResponse?.isExistingProfile ? "Yes" : "No"}
					/>
					<Field
						label="Quote Status"
						value={state?.quoteResponse ? "Loaded" : "Not loaded"}
					/>
					<Field
						label="Validation Payload"
						value={state?.validatePayload ? "Present" : "None"}
					/>
					<Field
						label="Transaction Response"
						value={state?.transactionResponse ? "Present" : "None"}
					/>
				</Section>

				{/* Pickup Location (Cash-Out) */}
				{state?.selectedLocation && (
					<Section
						title="Selected Location"
						isCollapsed={collapsed["Selected Location"]}
						onToggle={() => toggleSection("Selected Location")}
					>
						<Field label="Name" value={state.selectedLocation.name} />
						<Field label="Address" value={state.selectedLocation.address} />
						<Field label="City" value={state.selectedLocation.city} />
					</Section>
				)}

				{/* Cash-In Location (Deposit) */}
				<Section
					title="Cash-In Location"
					isCollapsed={collapsed["Cash-In Location"]}
					onToggle={() => toggleSection("Cash-In Location")}
				>
					{state?.cashInLocation ? (
						<>
							<Field
								label="Location ID"
								value={state.cashInLocation.locationId}
								copyable={true}
							/>
							<Field label="Name" value={state.cashInLocation.name} />
							<Field label="Address" value={state.cashInLocation.address} />
							<Field label="City" value={state.cashInLocation.city} />
							<Field label="State" value={state.cashInLocation.state} />
							<Field label="Country" value={state.cashInLocation.country} />
							<BooleanToggle
								label="Form Free"
								value={state.cashInLocation.formFreeCompletion}
								onChange={(value) => {
									if (state.cashInLocation) {
										rampsActions.setCashInLocation({
											...state.cashInLocation,
											formFreeCompletion: value,
										});
									}
								}}
							/>
							<Field label="Latitude" value={state.cashInLocation.latitude} />
							<Field label="Longitude" value={state.cashInLocation.longitude} />
							<div className="col-span-2 mt-2">
								<button
									type="button"
									onClick={() => rampsActions.clearCashInLocation()}
									className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
								>
									Clear Location
								</button>
							</div>
						</>
					) : (
						<div className="col-span-2 text-xs text-gray-500">
							No cash-in location selected
						</div>
					)}
				</Section>

				{/* Profile Tracking */}
				<Section
					title="Profile Tracking"
					isCollapsed={collapsed["Profile Tracking"]}
					onToggle={() => toggleSection("Profile Tracking")}
				>
					<BooleanToggle
						label="Profile Created"
						value={state?.profileCreationCompleted ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								profileCreationCompleted: value,
							}))
						}
					/>
					<BooleanToggle
						label="Wallet Lookup Done"
						value={state?.walletLookupCompleted ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								walletLookupCompleted: value,
							}))
						}
					/>
					<BooleanToggle
						label="Phone Lookup Done"
						value={state?.phoneLookupCompleted ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								phoneLookupCompleted: value,
							}))
						}
					/>
					<BooleanToggle
						label="Wallet Associated"
						value={state?.walletAssociationCompleted ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								walletAssociationCompleted: value,
							}))
						}
					/>
				</Section>

				{/* KYC Verification */}
				<Section
					title="KYC Verification"
					isCollapsed={collapsed["KYC Verification"]}
					onToggle={() => toggleSection("KYC Verification")}
				>
					<BooleanToggle
						label="Step Up Required"
						value={state?.isStepUpRequired ?? false}
						onChange={(value) => rampsActions.setStepUpRequired(value)}
					/>
					<BooleanToggle
						label="Verification Completed"
						value={state?.kycVerificationCompleted ?? false}
						onChange={(value) =>
							rampsStore.setState((prev) => ({
								...prev,
								kycVerificationCompleted: value,
							}))
						}
					/>
					<BooleanToggle
						label="KYC Flow Enabled"
						value={state?.kycFlowEnabled ?? false}
						onChange={(value) => rampsActions.setKycFlowEnabled(value)}
					/>
					<EditableNumberField
						label="KYC Polling Time"
						value={state?.kycPollingTime}
						onChange={(value) => rampsActions.setKycPollingTime(value)}
					/>
					<MaskedField label="KYC Token" value={state?.kycToken || ""} />
					<Field
						label="Applicant ID"
						value={state?.kycApplicantId || "—"}
						copyable={true}
					/>
					<EditableDropdown
						label="Step Up Required"
						value={state?.isStepUpRequired ? "true" : "false"}
						options={["true", "false"]}
						onChange={(value) =>
							rampsActions.setStepUpRequired(value === "true")
						}
					/>
					<EditableDropdown
						label="Allowed To Proceed"
						value={state?.isAllowedToProceed ? "true" : "false"}
						options={["true", "false"]}
						onChange={(value) =>
							rampsActions.setIsAllowedToProceed(value === "true")
						}
					/>
					{state?.kycVerificationData && (
						<div className="col-span-2 mt-2">
							<div className="text-xs font-semibold text-gray-400 mb-2">
								Verification Data:
							</div>
							<div className="text-xs text-gray-200">
								<pre className="bg-gray-800 p-2 rounded overflow-x-auto">
									{JSON.stringify(state.kycVerificationData, null, 2)}
								</pre>
							</div>
						</div>
					)}
				</Section>

				{/* Locks */}
				{state?.locks && state.locks.size > 0 && (
					<Section
						title="Locked Fields"
						isCollapsed={collapsed["Locked Fields"]}
						onToggle={() => toggleSection("Locked Fields")}
					>
						<div className="text-sm text-gray-300">
							{Array.from(state.locks).join(", ")}
						</div>
					</Section>
				)}
			</div>
		</div>
	);
}

function Section({
	title,
	children,
	isCollapsed = false,
	onToggle,
}: {
	title: string;
	children: React.ReactNode;
	isCollapsed?: boolean;
	onToggle?: () => void;
}) {
	return (
		<div className="border-b border-gray-700 pb-4 last:border-0">
			<button
				type="button"
				onClick={onToggle}
				className="w-full flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity group"
			>
				{isCollapsed ? (
					<ChevronRight className="w-4 h-4 text-blue-400 transition-transform group-hover:translate-x-0.5" />
				) : (
					<ChevronDown className="w-4 h-4 text-blue-400 transition-transform group-hover:translate-y-0.5" />
				)}
				<h3 className="text-sm font-bold text-blue-400">{title}</h3>
			</button>
			{!isCollapsed && (
				<div className="grid gap-2 grid-cols-[1fr_2fr]">{children}</div>
			)}
		</div>
	);
}

function Field({
	label,
	value,
	copyable = false,
}: {
	label: string;
	value?: string | number | null;
	copyable?: boolean;
}) {
	const displayValue = value ?? "—";
	const isEmpty = !value || value === "";

	const handleCopy = () => {
		if (value) {
			navigator.clipboard.writeText(String(value));
		}
	};

	return (
		<>
			<div className="text-xs font-semibold text-gray-400 whitespace-nowrap">
				{label}
			</div>
			<div className="flex items-center gap-2 group">
				<span
					className={`text-xs flex-1 ${isEmpty ? "text-gray-600" : "text-gray-200"}`}
				>
					{displayValue}
				</span>
				{copyable && !isEmpty && (
					<button
						type="button"
						onClick={handleCopy}
						className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded transition-opacity"
						title="Copy"
					>
						<Copy className="w-3 h-3 text-gray-400" />
					</button>
				)}
			</div>
		</>
	);
}

function MaskedField({
	label,
	value,
	masked = true,
}: {
	label: string;
	value: string;
	masked?: boolean;
}) {
	const [isRevealed, setIsRevealed] = useState(false);
	const displayValue = value || "—";
	const isEmpty = !value || value === "";

	const handleCopy = () => {
		navigator.clipboard.writeText(value);
	};

	return (
		<>
			<div className="text-xs font-semibold text-gray-400 whitespace-nowrap">
				{label}
			</div>
			<div className="flex items-center gap-2 group">
				<span
					className={`text-xs flex-1 ${isEmpty ? "text-gray-600" : "text-gray-200"}`}
				>
					{masked && !isRevealed && !isEmpty ? "••••••" : displayValue}
				</span>
				{!isEmpty && (
					<>
						{masked && (
							<button
								type="button"
								onClick={() => setIsRevealed(!isRevealed)}
								className="px-1.5 py-0.5 text-xs opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded transition-opacity"
								title={isRevealed ? "Hide" : "Show"}
							>
								{isRevealed ? "Hide" : "Show"}
							</button>
						)}
						<button
							type="button"
							onClick={handleCopy}
							className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded transition-opacity"
							title="Copy"
						>
							<Copy className="w-3 h-3 text-gray-400" />
						</button>
					</>
				)}
			</div>
		</>
	);
}

function BooleanToggle({
	label,
	value,
	onChange,
}: {
	label: string;
	value: boolean;
	onChange: (newValue: boolean) => void;
}) {
	return (
		<>
			<div className="text-xs font-semibold text-gray-400 whitespace-nowrap">
				{label}
			</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={() => onChange(!value)}
					className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
						value ? "bg-blue-600" : "bg-gray-600"
					}`}
					aria-pressed={value}
				>
					<span
						className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
							value ? "translate-x-5" : "translate-x-1"
						}`}
					/>
				</button>
				<span
					className={`text-xs ${value ? "text-green-400" : "text-gray-500"}`}
				>
					{value ? "Yes" : "No"}
				</span>
			</div>
		</>
	);
}

function EditableTextField({
	label,
	value,
	onChange,
}: {
	label: string;
	value: string;
	onChange: (newValue: string) => void;
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(value);

	const handleSave = () => {
		onChange(editValue);
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditValue(value);
		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSave();
		} else if (e.key === "Escape") {
			handleCancel();
		}
	};

	return (
		<>
			<div className="text-xs font-semibold text-gray-400 whitespace-nowrap">
				{label}
			</div>
			{isEditing ? (
				<div className="flex items-center gap-1">
					<input
						type="text"
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						onKeyDown={handleKeyDown}
						className="flex-1 text-xs bg-gray-800 text-gray-200 border border-blue-500 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
					/>
					<button
						type="button"
						onClick={handleSave}
						className="p-1 hover:bg-gray-700 rounded"
						title="Save"
					>
						<Check className="w-3 h-3 text-green-400" />
					</button>
					<button
						type="button"
						onClick={handleCancel}
						className="p-1 hover:bg-gray-700 rounded"
						title="Cancel"
					>
						<X className="w-3 h-3 text-red-400" />
					</button>
				</div>
			) : (
				<div className="flex items-center gap-2 group">
					<span
						className={`text-xs flex-1 ${!value || value === "" ? "text-gray-600" : "text-gray-200"}`}
					>
						{value || "—"}
					</span>
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded transition-opacity"
						title="Edit"
					>
						<Edit2 className="w-3 h-3 text-blue-400" />
					</button>
				</div>
			)}
		</>
	);
}

function EditableNumberField({
	label,
	value,
	onChange,
}: {
	label: string;
	value: number | undefined;
	onChange: (newValue: number) => void;
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(value?.toString() || "");

	const handleSave = () => {
		const num = Number.parseFloat(editValue);
		if (!Number.isNaN(num)) {
			onChange(num);
		}
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditValue(value?.toString() || "");
		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSave();
		} else if (e.key === "Escape") {
			handleCancel();
		}
	};

	return (
		<>
			<div className="text-xs font-semibold text-gray-400 whitespace-nowrap">
				{label}
			</div>
			{isEditing ? (
				<div className="flex items-center gap-1">
					<input
						type="number"
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						onKeyDown={handleKeyDown}
						className="flex-1 text-xs bg-gray-800 text-gray-200 border border-blue-500 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
					/>
					<button
						type="button"
						onClick={handleSave}
						className="p-1 hover:bg-gray-700 rounded"
						title="Save"
					>
						<Check className="w-3 h-3 text-green-400" />
					</button>
					<button
						type="button"
						onClick={handleCancel}
						className="p-1 hover:bg-gray-700 rounded"
						title="Cancel"
					>
						<X className="w-3 h-3 text-red-400" />
					</button>
				</div>
			) : (
				<div className="flex items-center gap-2 group">
					<span
						className={`text-xs flex-1 ${!value ? "text-gray-600" : "text-gray-200"}`}
					>
						{value ?? "—"}
					</span>
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded transition-opacity"
						title="Edit"
					>
						<Edit2 className="w-3 h-3 text-blue-400" />
					</button>
				</div>
			)}
		</>
	);
}

function EditableDropdown({
	label,
	value,
	options,
	onChange,
}: {
	label: string;
	value: string;
	options: string[];
	onChange: (newValue: string) => void;
}) {
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(value);

	const handleSave = () => {
		onChange(editValue);
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditValue(value);
		setIsEditing(false);
	};

	return (
		<>
			<div className="text-xs font-semibold text-gray-400 whitespace-nowrap">
				{label}
			</div>
			{isEditing ? (
				<div className="flex items-center gap-1">
					<select
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						className="flex-1 text-xs bg-gray-800 text-gray-200 border border-blue-500 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
					>
						{options.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
					<button
						type="button"
						onClick={handleSave}
						className="p-1 hover:bg-gray-700 rounded"
						title="Save"
					>
						<Check className="w-3 h-3 text-green-400" />
					</button>
					<button
						type="button"
						onClick={handleCancel}
						className="p-1 hover:bg-gray-700 rounded"
						title="Cancel"
					>
						<X className="w-3 h-3 text-red-400" />
					</button>
				</div>
			) : (
				<div className="flex items-center gap-2 group">
					<span
						className={`text-xs flex-1 ${!value || value === "" ? "text-gray-600" : "text-gray-200"}`}
					>
						{value || "—"}
					</span>
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-700 rounded transition-opacity"
						title="Edit"
					>
						<Edit2 className="w-3 h-3 text-blue-400" />
					</button>
				</div>
			)}
		</>
	);
}

export default {
	name: "Ramps Store",
	render: <DevtoolPanel />,
};
